/**
 * Set a new password from a reset link.
 *
 * The token comes from the query string. Succeeding revokes every existing
 * session for the account, so the user signs in fresh afterwards.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, ErrorAlert, PasswordInput } from '../../components/index.js';
import { authApi } from '../../services/index.js';
import { useMutation } from '../../hooks/useApi.js';

export function ResetPasswordPage() {
  const { t } = useTranslation('administration');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [done, setDone] = useState(false);

  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;

  const { mutate, pending, error, fieldErrors } = useMutation(async () => {
    await authApi.resetPassword({ token, password: form.password });
    setDone(true);
  });

  if (!token) {
    return (
      <Card>
        <div className="stack">
          <h1 className="auth-title">{t('auth.reset.invalidTitle')}</h1>
          <Alert variant="danger">{t('auth.reset.invalidMessage')}</Alert>
          <Link to="/forgot-password" className="btn btn-primary btn-block">
            {t('auth.reset.requestNew')}
          </Link>
        </div>
      </Card>
    );
  }

  if (done) {
    return (
      <Card>
        <div className="stack">
          <h1 className="auth-title">{t('auth.reset.doneTitle')}</h1>
          <p className="auth-subtitle">{t('auth.reset.doneMessage')}</p>
          <Button variant="primary" size="lg" block onClick={() => navigate('/login', { replace: true })}>
            {t('auth.reset.goToSignIn')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (mismatch) return;
          mutate().catch(() => {});
        }}
        noValidate
      >
        <div>
          <h1 className="auth-title">{t('auth.reset.title')}</h1>
          <p className="auth-subtitle">{t('auth.reset.subtitle')}</p>
        </div>

        <ErrorAlert error={error} />

        <PasswordInput
          label={t('auth.password.new')}
          autoComplete="new-password"
          required
          autoFocus
          value={form.password}
          hint={t('auth.password.rules')}
          error={fieldErrors.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />

        <PasswordInput
          label={t('auth.password.confirm')}
          autoComplete="new-password"
          required
          value={form.confirm}
          error={mismatch ? t('auth.password.mismatch') : undefined}
          onChange={(event) => setForm({ ...form, confirm: event.target.value })}
        />

        <Button type="submit" variant="primary" size="lg" block loading={pending} disabled={mismatch}>
          {t('auth.reset.submit')}
        </Button>
      </form>
    </Card>
  );
}
